import { createSlice } from '@reduxjs/toolkit';
import ApiClientConnector from "src/api/ApiClientConnector";
import { convertToB2BMarkupSummary } from "./actions/MarkupActions";

const initialState = {
    markups: [],
    markupSummaryList: [],
    isMarkupDetailsView: null,
    selectedMarkup: null,
    isMarkupCreated: false,
    isMarkupLoading: false,
    selectedItem: null,
    isDetailViewOfMarkupCriteria: false,
    markupPriorities: [],
    markupCodes: [],
};

const MarkupManagerSlice = createSlice({
    name: 'markups',
    initialState,
    reducers: {
        setMarkups: (state, action) => {
            state.markups = action.payload;
        },
        setMarkupSummaryList: (state, action) => {
            state.markupSummaryList = action.payload;
        },
        changeMarkupDetailsView: (state, action) => {
            state.isMarkupDetailsView = action.payload;
        },
        setSelectedMarkup: (state, action) => {
            state.selectedMarkup = action.payload;
        },
        setIsMarkupCreated: (state, action) => {
            state.isMarkupCreated = action.payload;
        },
        clearSelectedMarkup: (state) => {
            state.selectedMarkup = null;
            state.isMarkupLoading = false;
        },
        fetchMarkupByIdStart: (state) => {
            state.isMarkupLoading = true;
        },
        fetchMarkupByIdSuccess: (state, action) => {
            state.selectedMarkup = action.payload;
            state.isMarkupLoading = false;
        },
        fetchMarkupByIdFailure: (state) => {
            state.isMarkupLoading = false;
        },

        updateMarkupSummaryById: (state, action) => {
            if (!state.markupSummaryList?.B2BMarkupSummaries) return;

            const updated = action.payload;
            state.markupSummaryList.B2BMarkupSummaries =
                state.markupSummaryList.B2BMarkupSummaries.map(item =>
                    item.Id === updated.Id ? { ...item, ...updated } : item
                );
        },
        setSelectedItem: (state, action) => {
            state.selectedItem = action.payload;
        },
        changeDetailViewOfMarkupCriteria: (state, action) => {
            state.isDetailViewOfMarkupCriteria = action.payload;
        },
        setMarkupPriorities: (state, action) => {
            state.markupPriorities = action.payload ?? [];
        },
        setMarkupCodes: (state, action) => {
            state.markupCodes = action.payload ?? [];
        }
    },
});

export const {
    setMarkups,
    setMarkupSummaryList,
    changeMarkupDetailsView,
    setSelectedMarkup,
    clearSelectedMarkup,
    fetchMarkupByIdStart,
    fetchMarkupByIdSuccess,
    fetchMarkupByIdFailure,
    updateMarkupSummaryById,
    setSelectedItem,
    setIsMarkupCreated,
    changeDetailViewOfMarkupCriteria,
    setMarkupPriorities,
    setMarkupCodes,
} = MarkupManagerSlice.actions;

export const addMarkup =
    (instance, accounts, markupRequest) => async (dispatch) => {
        const client = await ApiClientConnector(instance, accounts);
        const result = await client.markups.createMarkup(markupRequest);

        dispatch(setSelectedMarkup(result));
        dispatch(setIsMarkupCreated(true));
        return result;
    };

export const updateMarkup =
    (instance, accounts, markupId, markupRequest) => async (dispatch) => {
        const client = await ApiClientConnector(instance, accounts);

        const updatedMarkup = await client.markups.updateMarkup(
            markupId,
            markupRequest
        );

        dispatch(fetchMarkupByIdSuccess(updatedMarkup));

        const summary = {
            Id: updatedMarkup.Id,
            Name: updatedMarkup.Name,
            Code: updatedMarkup.Code,
            Type: updatedMarkup.Type,
            Priority: updatedMarkup.Priority,
            Status:
                typeof updatedMarkup.Status === 'boolean'
                    ? updatedMarkup.Status ? 'Active' : 'Inactive'
                    : updatedMarkup.Status,
            DateBandCount: updatedMarkup.DateBands?.length ?? 0,
        };

        dispatch(updateMarkupSummaryById(summary));

        return updatedMarkup;
    };

export const deleteDateBand =
  (instance, accounts, dateBandId) => async () => {
    const client = await ApiClientConnector(instance, accounts);
    await client.markups.deleteDateBand(dateBandId);
};

export const fetchMarkupById =
    (instance, accounts, markupId) => async (dispatch) => {
        dispatch(fetchMarkupByIdStart());

        try {
            const client = await ApiClientConnector(instance, accounts);
            const result = await client.markups.getMarkupById(markupId);
            dispatch(fetchMarkupByIdSuccess(result));
            return result;
        } catch (error) {
            dispatch(fetchMarkupByIdFailure());
            throw error;
        }
    };

export const fetchMarkupSummary =
    (instance, accounts) => async (dispatch) => {
        const client = await ApiClientConnector(instance, accounts);
        const result = await client.markups.markupSummaryList();
        const transformed = convertToB2BMarkupSummary(result);
        dispatch(setMarkupSummaryList(transformed));
        return transformed?.B2BMarkupSummaries;
    };

export const addMarkupCriteria = (instance, accounts, payload) => async () => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.markups.createMarkupCriteria(payload);
    return result;
};

export const fetchMarkupPriorities = (instance, accounts) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.markups.markupPriority();
    dispatch(setMarkupPriorities(result));
    return result;
};

export const fetchMarkupCodes = (instance, accounts) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.markups.markupCodes();
    dispatch(setMarkupCodes(result));
    return result;
};

export default MarkupManagerSlice.reducer;
